import React, { useState, useEffect, useCallback } from 'react';
import { lifetimeRewardsAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';

const rankConfigs = {
  STAR: {
    matchingRequired: 100000,
    reward: 5000,
    color: "bg-green-500",
  },
  SILVER_STAR: {
    matchingRequired: 250000,
    reward: 12500,
    color: "bg-gray-400",
  },
  GOLD: {
    matchingRequired: 500000,
    reward: 25000,
    color: "bg-yellow-500",
  },
  PLATINUM: {
    matchingRequired: 1000000,
    reward: 50000,
    color: "bg-purple-500",
  },
  DIAMOND: {
    matchingRequired: 2500000,
    reward: 125000,
    color: "bg-blue-500",
  },
  BLUE_DIAMOND: {
    matchingRequired: 5000000,
    reward: 250000,
    color: "bg-cyan-500",
  },
  ROYAL_DIAMOND: {
    matchingRequired: 10000000, // 1 Cr
    reward: 500000,
    color: "bg-indigo-500",
  },
  CROWN_DIAMOND: {
    matchingRequired: 20000000, // 2 Cr
    reward: 1000000,
    color: "bg-orange-500",
  },
  CROWN_AMBASSADOR: {
    matchingRequired: 50000000, // 5 Cr
    reward: 2500000,
    color: "bg-red-500",
  },
  DOUBLE_CROWN_STAR: {
    matchingRequired: 150000000, // 15 Cr
    reward: 7500000, // 75 Lacs
    color: "bg-pink-500",
  },
  DIRECTOR_STAR: {
    matchingRequired: 1000000000, // 100 Cr
    reward: 50000000, // 5 Cr
    color: "bg-violet-600",
  },
  DIRECTOR_CLUB: {
    matchingRequired: 5000000000, // 500 Cr
    reward: 250000000, // 25 Cr
    color: "bg-black",
  },
};

// Sequential reward order
const sequentialRewardOrder = ['STAR', 'SILVER_STAR', 'GOLD', 'PLATINUM', 'DIAMOND', 
  'BLUE_DIAMOND', 'ROYAL_DIAMOND', 'CROWN_DIAMOND', 'CROWN_AMBASSADOR', 
  'DOUBLE_CROWN_STAR', 'DIRECTOR_STAR', 'DIRECTOR_CLUB'];

const UserLifetimeRewards = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claimingBonus, setClaimingBonus] = useState(false);

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  }

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await lifetimeRewardsAPI.getMyDashboard();
      if (response.data.success) {
        const payload = response.data.data;
        setData(payload);
        if (payload.rankAchievementThisRun?.newRank) {
          const { newRank, rewardAmount } = payload.rankAchievementThisRun;
          alert(
            `Congratulations! You achieved ${newRank.replace(/_/g, ' ')} rank. Lifetime reward amount: ${formatCurrency(
              rewardAmount
            )}. It will be credited after admin distribution.`
          );
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load lifetime rewards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleClaimMatchingBonus = async () => {
    if (!data?.canClaimMatchingBonus || (data?.matchingBonusPreviewAmount ?? 0) <= 0) {
      return;
    }
    if (
      !window.confirm(
        `Claim ${formatCurrency(data.matchingBonusPreviewAmount)} (5% of your current matching business) into your Lifetime Rewards income wallet?`
      )
    ) {
      return;
    }
    try {
      setClaimingBonus(true);
      setError('');
      const response = await lifetimeRewardsAPI.claimMatchingBonus();
      if (response.data.success) {
        const { creditedAmount, lifeTimeRewardsWallet } = response.data.data;
        alert(
          `Success! ${formatCurrency(creditedAmount)} credited. Your lifetime rewards wallet balance is now ${formatCurrency(lifeTimeRewardsWallet)}.`
        );
        await fetchDashboard();
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to claim bonus';
      setError(msg);
      alert(msg);
    } finally {
      setClaimingBonus(false);
    }
  };

  const getRankColor = (rank) => rankConfigs[rank]?.color || 'bg-gray-500';

  const displayName = (member) => {
    const populated = member.userId;
    if (populated && typeof populated === 'object' && populated.name) {
      return populated.name;
    }
    return member.name || '—';
  };

  const displayCode = (member) => {
    const populated = member.userId;
    if (populated && typeof populated === 'object' && populated.mySponsorCode) {
      return populated.mySponsorCode;
    }
    return member.sponsorCode || '—';
  };

  const memberKey = (member, index) => {
    const id = member.userId?._id || member.userId;
    return id ? String(id) : `idx-${index}`;
  };

  if (loading && !data) {
    return <LoadingSpinner />;
  }

  const leg = data?.legStructure;
  const powerUsers = leg?.powerLeg?.users || [];
  const supportUsers = leg?.supportLeg?.users || [];

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Lifetime Rewards</h1>
          <p className="text-gray-600">
            Volumes use each person&apos;s <strong>total business (self + downline)</strong>.
            Power leg = your direct with the highest total business. Rest of legs = all other directs&apos;
            total business combined. Matching business is the lower of the two and drives rank and the 5% claim.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchDashboard()}
          disabled={loading}
          className="shrink-0 px-4 py-2 bg-[#0F2F3D] text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm font-medium"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {data && (
        <>
          <div
            className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
              data.sequentialRewards?.qualifiesForCurrentReward
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}
          >
            {data.sequentialRewards?.qualifiesForCurrentReward ? (
              <span>
                🎉 You are <strong>qualified</strong> for your current lifetime reward ({data.sequentialRewards?.currentRewardRank?.replace(/_/g, ' ')})!
                {data.sequentialRewards?.carriedForwardBalance > 0 ? (
                  <span className="ml-1 text-green-900 font-semibold">
                    {' '}{formatCurrency(data.sequentialRewards.carriedForwardBalance)} remaining balance is carried forward toward your next reward!
                  </span>
                ) : (
                  <span> Your available matching income has reached the required amount.</span>
                )}
              </span>
            ) : (
              <span>
                📈 You need to generate <strong>more matching income</strong> to qualify for your current reward. Keep building your team and increasing business.
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
              <h3 className="text-sm font-medium text-gray-500">Direct sponsors</h3>
              <p className="text-2xl font-bold text-gray-900">{data.user?.directSponseesCount ?? 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-blue-100">
              <h3 className="text-sm font-medium text-gray-500">My own purchases</h3>
              <p className="text-md font-bold text-blue-600">{formatCurrency(data.user?.ownBusiness)}</p>
              <p className="text-xs text-gray-500 mt-1">Your confirmed orders</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-indigo-100">
              <h3 className="text-sm font-medium text-gray-500">My total business</h3>
              <p className="text-md font-bold text-indigo-600">{formatCurrency(data.user?.totalBusiness)}</p>
              <p className="text-xs text-gray-500 mt-1">Self + downline</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-green-100">
              <h3 className="text-sm font-medium text-gray-500">Power leg</h3>
              <p className="text-md font-bold text-green-600">
                {formatCurrency(leg?.powerLeg?.totalBusiness)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Strongest direct&apos;s total business (self + downline)</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-purple-100">
              <h3 className="text-sm font-medium text-gray-500">Rest of legs</h3>
              <p className="text-md font-bold text-purple-600">
                {formatCurrency(leg?.supportLeg?.totalBusiness)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Other directs&apos; total business (self + downline)</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-orange-100">
              <h3 className="text-sm font-medium text-gray-500">Total matching business</h3>
              <p className="text-md font-bold text-orange-600">{formatCurrency(leg?.matchingBusiness)}</p>
              <p className="text-xs text-gray-500 mt-1">min(power, rest) - Total</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#0F2F3D] to-[#1a4d63] rounded-xl shadow-lg border border-white/10 p-6 mb-6 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-lg font-semibold mb-1">Matching bonus (5%)</h2>
                <p className="text-sm text-white/80 max-w-xl">
                  Claim <strong>5%</strong> of your current matching business into{' '}
                  <strong>wallet → income → Lifetime Rewards</strong>. You can use this once per calendar month.
                </p>
                <p className="text-sm mt-3">
                  Total Lifetime Rewards received:{' '}
                  <strong className="text-lg">{formatCurrency(data.lifeTimeRewardsWallet)}</strong>
                </p>
                {data.lastMatchingBonusClaimAt && !data.canClaimMatchingBonus && (
                  <p className="text-xs text-amber-200 mt-2">
                    Last claim: {new Date(data.lastMatchingBonusClaimAt).toLocaleString()} — next claim next
                    calendar month.
                  </p>
                )}
              </div>
             
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
              <h3 className="text-sm font-medium text-gray-500">Total directs&apos; own purchases</h3>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(leg?.totalTeamBusiness)}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
              <h3 className="text-sm font-medium text-gray-500">Your sponsor code</h3>
              <p className="text-xl font-bold text-[#0F2F3D]">{data.user?.mySponsorCode}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
              <h3 className="text-sm font-medium text-gray-500">Last updated</h3>
              <p className="text-sm font-medium text-gray-900">
                {data.lastCalculatedAt
                  ? new Date(data.lastCalculatedAt).toLocaleString()
                  : '—'}
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Matching Income & Carry Forward Details</h2>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Total Matching Business</h3>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(leg?.matchingBusiness)}</p>
                <p className="text-xs text-gray-500">Cumulative min(power, rest)</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Business Consumed</h3>
                <p className="text-xl font-bold text-gray-700">{formatCurrency(data.sequentialRewards?.totalBusinessConsumed || 0)}</p>
                <p className="text-xs text-gray-500">Used by completed rewards</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Available Matching Income</h3>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(data.sequentialRewards?.businessForCurrentReward || 0)}</p>
                <p className="text-xs text-gray-500">Eligible for current & next rewards</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Carry Forward to Next</h3>
                <p className="text-xl font-bold text-purple-600">{formatCurrency(data.sequentialRewards?.carriedForwardBalance || 0)}</p>
                <p className="text-xs text-gray-500">Balance toward next rank</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow border border-green-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-green-100 bg-green-50">
                <h2 className="text-lg font-semibold text-green-900">Power leg (strongest direct — own purchases)</h2>
              </div>
              <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                {powerUsers.length === 0 ? (
                  <p className="text-sm text-gray-500">No direct sponsors yet.</p>
                ) : (
                  powerUsers.map((member, index) => (
                    <div
                      key={memberKey(member, index)}
                      className="flex justify-between items-center text-sm border-b border-gray-50 pb-2"
                    >
                      <div>
                        <span className="font-medium text-green-800">{displayName(member)}</span>
                        <span className="text-gray-500 ml-2">({displayCode(member)})</span>
                      </div>
                      <span className="font-bold text-green-600">{formatCurrency(member.business)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border border-purple-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-purple-100 bg-purple-50">
                <h2 className="text-lg font-semibold text-purple-900">Rest of legs (other directs)</h2>
              </div>
              <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                {supportUsers.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No additional directs, or only one direct total — rest of legs needs other directs with
                    volume.
                  </p>
                ) : (
                  supportUsers.map((member, index) => (
                    <div
                      key={memberKey(member, index)}
                      className="flex justify-between items-center text-sm border-b border-gray-50 pb-2"
                    >
                      <div>
                        <span className="font-medium text-purple-800">{displayName(member)}</span>
                        <span className="text-gray-500 ml-2">({displayCode(member)})</span>
                      </div>
                      <span className="font-bold text-purple-600">{formatCurrency(member.business)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Current rank</h2>
            </div>
            <div className="p-4 flex items-center gap-3">
              <span
                className={`px-4 py-2 text-sm font-medium text-white rounded-full ${getRankColor(
                  data.currentRank || 'NONE'
                )}`}
              >
                {(data.currentRank || 'NONE').replace(/_/g, ' ')}
              </span>
              <p className="text-sm text-gray-600">
                Rank is based on matching business vs each tier threshold. Reward amounts are credited by the
                admin after distribution.
              </p>
            </div>
          </div>

          {/* Sequential Reward Status */}
          {data.sequentialRewards && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow border border-blue-200 overflow-hidden mb-6">
              <div className="px-4 py-3 border-b border-blue-200">
                <h2 className="text-lg font-semibold text-gray-900">Sequential Reward Status</h2>
              </div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Reward Index:</span>
                  <span className="font-medium">{data.sequentialRewards.currentRewardIndex || 0} / {sequentialRewardOrder.length - 1}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Reward:</span>
                  <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getRankColor(data.sequentialRewards.currentRewardRank || 'NONE')}`}>
                    {(data.sequentialRewards.currentRewardRank || 'NONE').replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Reward Index:</span>
                  <span className="font-medium">{data.sequentialRewards.currentRewardIndex || 0} / {sequentialRewardOrder.length - 1}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Reward:</span>
                  <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getRankColor(data.sequentialRewards.currentRewardRank || 'NONE')}`}>
                    {(data.sequentialRewards.currentRewardRank || 'NONE').replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Available Matching Income for Current Reward:</span>
                  <span className="font-bold text-blue-600">{formatCurrency(data.sequentialRewards.businessForCurrentReward || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Required for Current Reward:</span>
                  <span className="font-medium">{formatCurrency(data.sequentialRewards.currentRewardConfig?.matchingRequired || 0)}</span>
                </div>
                {data.sequentialRewards.carriedForwardBalance > 0 && (
                  <div className="flex justify-between bg-purple-50 p-1.5 rounded">
                    <span className="text-purple-800 font-medium">Carried Forward to Next Reward:</span>
                    <span className="font-bold text-purple-700">{formatCurrency(data.sequentialRewards.carriedForwardBalance)}</span>
                  </div>
                )}
                {data.sequentialRewards.totalBusinessConsumed > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Business Consumed by Completed Rewards:</span>
                    <span className="font-medium text-gray-700">{formatCurrency(data.sequentialRewards.totalBusinessConsumed)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Different Rewards Received:</span>
                  <span className="font-bold text-green-600">{data.sequentialRewards.totalRewardsDistributed || 0} of 12</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Explanation:</span>
                  <span className="text-xs text-gray-500">12 different reward levels (STAR → DIRECTOR_CLUB)</span>
                </div>
                {data.sequentialRewards.nextRewardRank && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Next Eligible Reward:</span>
                    <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getRankColor(data.sequentialRewards.nextRewardRank)}`}>
                      {data.sequentialRewards.nextRewardRank.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}
                {data.sequentialRewards.lastRewardResetAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Business Distribution / Reset:</span>
                    <span className="text-xs text-gray-500">{new Date(data.sequentialRewards.lastRewardResetAt).toLocaleString()}</span>
                  </div>
                )}
                {data.sequentialRewards.hasPendingRewards && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending Rewards:</span>
                    <span className="text-xs text-amber-600 font-medium">Yes - Awaiting distribution</span>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs text-gray-600 italic">
                    * There are 12 different rewards (STAR, SILVER_STAR, GOLD, etc.). Each reward can only be received once.
                  </p>
                  <p className="text-xs text-gray-600 italic mt-1">
                    * Rewards are distributed sequentially. Any remaining eligible balance after achieving a reward is automatically carried forward toward the next reward.
                  </p>
                  {data.sequentialRewards.hasPendingRewards && (
                    <p className="text-xs text-amber-600 font-medium mt-2">
                      ⚠️ You have pending rewards awaiting admin distribution. Once distributed, any carried-forward balance will immediately apply to your next rank.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Sequential Reward Progress</h2>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                There are 12 different rewards (STAR, SILVER_STAR, GOLD, etc.). Each reward can only be received once. You must complete them in sequential order - one at a time.
              </p>
              <div className="space-y-4">
                {sequentialRewardOrder.map((rank, index) => {
                  const config = rankConfigs[rank];
                  const achievement = data.rankAchievements?.find((ach) => ach.rank === rank);
                
                // Calculate progress based on sequential business
                let progress = 0;
                let currentBusiness = 0;
                let requiredBusiness = config.matchingRequired;
                
                if (data.sequentialRewards) {
                  const currentIndex = data.sequentialRewards.currentRewardIndex || 0;
                  if (index === currentIndex) {
                    // This is the current reward being worked toward
                    currentBusiness = data.sequentialRewards.businessForCurrentReward || 0;
                    progress = Math.min((currentBusiness / requiredBusiness) * 100, 100);
                  } else if (index < currentIndex) {
                    // Already completed
                    progress = 100;
                    currentBusiness = requiredBusiness;
                  } else {
                    // Future reward - not started yet
                    progress = 0;
                    currentBusiness = 0;
                  }
                }

                const isCurrentReward = data.sequentialRewards?.currentRewardRank === rank;
                const isCompleted = achievement?.rewardDistributed;
                const isPending = achievement && !achievement.rewardDistributed;
                const isLocked = index > (data.sequentialRewards?.currentRewardIndex || 0);
                
                // Only show current reward for pending status
                const isCurrentPending = isPending && isCurrentReward;

                return (
                  <div key={rank} className={`border rounded-lg p-4 ${isLocked ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                    <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium text-white rounded-full ${config.color}`}
                        >
                          {rank.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm text-gray-600">Reward: {formatCurrency(config.reward)}</span>
                        {isCurrentReward && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Current
                          </span>
                        )}
                        {isCompleted && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Distributed
                          </span>
                        )}
                        {isPending && (
                          <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                            {isCurrentReward ? 'Pending admin payout' : 'Pending - Complete current reward first'}
                          </span>
                        )}
                        {isLocked && (
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                            🔒 Locked
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1 flex-wrap gap-1">
                        <span>
                          {isLocked ? 'Locked - Complete previous rewards first' : `Available Matching Income: ${formatCurrency(currentBusiness)} / ${formatCurrency(requiredBusiness)}`}
                        </span>
                        <div className="flex items-center gap-2">
                          {isCurrentReward && data.sequentialRewards?.carriedForwardBalance > 0 && (
                            <span className="text-xs bg-purple-100 text-purple-800 font-semibold px-2 py-0.5 rounded">
                              Carry Forward: {formatCurrency(data.sequentialRewards.carriedForwardBalance)}
                            </span>
                          )}
                          <span className="font-semibold">{Math.round(progress)}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`${isCompleted ? 'bg-green-500' : isCurrentReward ? 'bg-blue-500' : 'bg-gray-400'} h-2 rounded-full transition-all`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                    {achievement && (
                      <div className="mt-2 text-xs text-gray-600">
                        Achieved: {new Date(achievement.achievedAt).toLocaleDateString()}
                        {achievement.businessUsed && (
                          <span className="text-blue-700 ml-2">
                            New Matching Income Used: {formatCurrency(achievement.businessUsed)}
                          </span>
                        )}
                        {achievement.matchingBusiness && (
                          <span className="text-orange-700 ml-2">
                            Total Matching: {formatCurrency(achievement.matchingBusiness)}
                          </span>
                        )}
                        {achievement.rewardDistributed && achievement.rewardDistributedAt && (
                          <span className="text-green-700 ml-2">
                            Paid: {new Date(achievement.rewardDistributedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserLifetimeRewards;
